import apiSvc, { BinaryFileType } from '../../../core/services/apiSvc';
import utilSvc from '../../../core/utilSvc';
import { HttpErrorIgnore } from '../../../../global';
import { SplitOrderInvoicesDataItem } from '../list/split_order_invoices_list_types';
import { SplitOrderInvoicesSearchModel } from '../list/search/split_order_invoices_search_types';
import {
    InvoiceLineItemDispensesDataItem,
    SplitOrderInvoicesDetailModel,
} from '../detail/split_order_invoices_detail_types';
import validSvc from '../../../core/validSvc';

const GET_DETAIL = 'invoice/split/detail';
const GET_BACKING_DISPENSES = 'invoice/split/backing';

const splitOrderInvoicesRepo = {
    getList: async (query: SplitOrderInvoicesSearchModel): Promise<SplitOrderInvoicesDataItem[]> => {
        const ceid = utilSvc.http.apiContextToAllArray(query.CE?.val);
        const rxid = utilSvc.http.apiContextToAllArray(query.RX?.val);
        const apiRequest = {
            Context: {
                CEID: ceid,
                RXID: rxid,
            },
            Params: {
                StartDate: utilSvc.dt.toIsoDate(query.StartDate?.val || ''),
                EndDate: utilSvc.dt.toIsoDate(query.EndDate?.val || ''),
                RebatesOnly: query.RebatesOnly || false,
            },
            Paging: {
                Size: 2000,
            },
            SortBy: [
                {
                    Field: 'VendorInvoiceDate',
                    Order: 'DSC',
                },
            ],
        };

        const response = await apiSvc.post('invoice/split/list', apiRequest);
        return response?.Entity?.data ?? response?.Entity ?? [];
    },

    excelExport: async (query: SplitOrderInvoicesSearchModel, filename: string): Promise<void> => {
        const ceid = utilSvc.http.apiContextToAllArray(query.CE?.val);
        const rxid = utilSvc.http.apiContextToAllArray(query.RX?.val);
        const apiRequest = {
            Context: {
                CEID: ceid,
                RXID: rxid,
            },
            Params: {
                StartDate: utilSvc.dt.toIsoDate(query.StartDate?.val || ''),
                EndDate: utilSvc.dt.toIsoDate(query.EndDate?.val || ''),
                RebatesOnly: query.RebatesOnly || false,
            },
        };

        await apiSvc.postGetBinary('invoice/split/excel', apiRequest, filename, BinaryFileType.Excel);
    },

    getDetail: async (id: string): Promise<SplitOrderInvoicesDetailModel> => {
        if (!validSvc.isText(id)) {
            throw new Error('splitOrderInvoicesRepo.getDetail, id is required');
        }

        return apiSvc.getEntity(GET_DETAIL, {
            Entity: {
                Params: {
                    Id: id,
                },
            },
        }) as unknown as SplitOrderInvoicesDetailModel;
    },

    getLineItemDispenses: async (invoiceId: string, lineId: string): Promise<InvoiceLineItemDispensesDataItem[]> => {
        if (!validSvc.isText(invoiceId) || !validSvc.isText(lineId)) {
            throw new Error('splitOrderInvoicesRepo.getLineItemDispenses, invoiceId and lineId are required');
        }

        return apiSvc.getEntityList<InvoiceLineItemDispensesDataItem>(GET_BACKING_DISPENSES, {
            Params: {
                InvoiceId: invoiceId,
                LineId: lineId,
            },
        });
    },

    getBackingDispensesExcel: async (
        invoiceId: string,
        invoiceNum: string,
        rebatesOnly: boolean,
    ): Promise<HttpErrorIgnore | undefined> => {
        if (!validSvc.isText(invoiceId)) return undefined;
        const filename = `Split-Invoice-${invoiceNum}-Backing-Dispenses`;
        const apiRequest = {
            Context: {},
            Params: {
                Id: invoiceId,
                RebatesOnly: rebatesOnly,
            },
        };
        return apiSvc.postGetBinary('invoice/split/backing/excel', apiRequest, filename, BinaryFileType.Excel);
    },
};

export default splitOrderInvoicesRepo;
